import { Box } from '@mui/material';
import { APP_NAME } from '../app/theme';
import { useFeatureReleased } from '../release';

/** "212Ez" is shown as its digits and its letters; a name without digits is shown whole. */
const splitName = (name: string): [string, string] => {
  const m = /^(\d+)(.*)$/.exec(name);
  return m ? [m[1]!, m[2]!] : ['', name];
};

/**
 * The name block wherever the app shows it (sign-in, password, sign-up, the header): the owner's
 * logo image (2026-11-02) while the switch `brand-lockup-image` is on, else the drawn block.
 */
export function AppLockup({ height = 64 }: { height?: number }) {
  const featureOn = useFeatureReleased();
  if (!featureOn('brand-lockup-image')) return <BrandLockup height={height} />;
  return (
    <Box
      component="img"
      src="/brand-lockup.png"
      alt={APP_NAME}
      title={`MVNO Cloud Solutions, powered by ${APP_NAME}: Telecom Made Easy`}
      height={height}
      width={Math.round((height * 1000) / 359)}
      sx={{ display: 'block', flexShrink: 0, maxWidth: '100%', objectFit: 'contain' }}
    />
  );
}

/** Sampled from the owner's logo: navy digits, sky-blue letters, a slate heading, a dark swoosh. */
export const LOCKUP = {
  heading: '#456585',
  powered: '#2E2E2E',
  tagline: '#3E4656',
  digits: '#1B3582',
  letters: '#4890D0',
  swoosh: '#2E3550',
  dot: '#1F3A80',
};

/**
 * The name block of 212Ez's own site (owner, 2026-10-28): "MVNO Cloud Solutions", then
 * "Powered By" beside the product name in heavy italic inside a swoosh, and "Telecom Made Easy"
 * under it. Drawn in code; the name comes from `APP_NAME`. Used while `brand-look` is on.
 */
export function BrandLockup({ height = 64 }: { height?: number }) {
  const [digits, letters] = splitName(APP_NAME);
  const font = '"Segoe UI", Calibri, Arial, sans-serif';
  return (
    <Box
      component="svg"
      role="img"
      aria-label={APP_NAME}
      viewBox="0 0 300 114"
      height={height}
      width={(height * 300) / 114}
      sx={{ display: 'block', flexShrink: 0, overflow: 'visible' }}
    >
      <title>{`MVNO Cloud Solutions, powered by ${APP_NAME}: Telecom Made Easy`}</title>
      <text
        x="2"
        y="24"
        fontFamily={font}
        fontSize="23"
        fontWeight="500"
        fill={LOCKUP.heading}
        textLength="262"
        lengthAdjust="spacingAndGlyphs"
      >
        MVNO Cloud Solutions
      </text>
      <text x="30" y="66" fontFamily={font} fontSize="12" fontWeight="500" fill={LOCKUP.powered}>
        Powered By
      </text>
      {/* The swoosh: from under the name, round its left end and over the top to a dot. */}
      <path
        d="M210 91C138 97 96 88 106 72C116 55 188 39 266 38"
        fill="none"
        stroke={LOCKUP.swoosh}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="270" cy="38" r="3.6" fill={LOCKUP.dot} />
      <text
        x="114"
        y="84"
        fontFamily={`"Arial Black", "Segoe UI Black", ${font}`}
        fontSize="46"
        fontWeight="900"
        fontStyle="italic"
        letterSpacing="-1.5"
        strokeWidth="1"
        paintOrder="stroke"
      >
        <tspan fill={LOCKUP.digits} stroke={LOCKUP.digits}>
          {digits}
        </tspan>
        <tspan fill={LOCKUP.letters} stroke={LOCKUP.letters}>
          {letters}
        </tspan>
      </text>
      <text
        x="150"
        y="110"
        fontFamily={font}
        fontSize="13.5"
        fontWeight="500"
        fill={LOCKUP.tagline}
        textLength="118"
        lengthAdjust="spacingAndGlyphs"
      >
        Telecom Made Easy
      </text>
    </Box>
  );
}
